import { useState } from 'react';
import { makeSeed } from '@fhnw/helpers/Array/seededRandom';

/** Data attributes used to carry the shuffle seed from SSR markup to the client. */
export const SHUFFLE_BLOCK_ATTR = 'data-shuffle-block';
export const SHUFFLE_SEED_ATTR = 'data-shuffle-seed';

const escapeSelector = (value: string): string =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value;

/** Read a seed previously rendered into the DOM for `blockId`, if any. */
const readSeedFromDom = (blockId: string): number | null => {
  const el = document.querySelector(
    `[${SHUFFLE_BLOCK_ATTR}="${escapeSelector(blockId)}"]`,
  );
  const raw = el?.getAttribute(SHUFFLE_SEED_ATTR);
  if (raw == null) {
    return null;
  }
  const seed = Number(raw);
  return Number.isFinite(seed) ? seed : null;
};

/**
 * Returns a stable shuffle seed that lets SSR and the client agree on the order
 * without a visible reshuffle.
 *
 * - On the **server** a fresh seed is generated and rendered into the markup
 *   (see {@link SHUFFLE_SEED_ATTR}).
 * - On the **client**, during hydration, the seed is read back from that markup
 *   so the first client render reproduces the server's exact order — no
 *   hydration mismatch and no flicker.
 * - On a client-only render with no server markup (e.g. SPA navigation) a fresh
 *   seed is generated, which is safe because there is nothing to hydrate against.
 *
 * The seed is fixed for the lifetime of the mount (lazy `useState` initializer),
 * so re-renders never reshuffle.
 *
 * @param blockId Stable block id, used to locate this block's seed in the DOM.
 * @param enabled When false, returns null and no shuffling should happen.
 */
export function useShuffleSeed(
  blockId: string,
  enabled: boolean,
): number | null {
  const [seed] = useState<number | null>(() => {
    if (!enabled) {
      return null;
    }
    if (typeof window === 'undefined') {
      return makeSeed();
    }
    return readSeedFromDom(blockId) ?? makeSeed();
  });
  return seed;
}
